import CheckIcon from '@material-design-icons/svg/filled/check.svg?react';
import ContentCopyIcon from '@material-design-icons/svg/filled/content_copy.svg?react';
import ExpandMoreIcon from '@material-design-icons/svg/filled/expand_more.svg?react';
import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import { ReactNode, useEffect, useId, useState } from 'react';
import { toast } from 'sonner';

interface SectionProps {
  title: string;
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Section({
  title,
  trailing,
  children,
  className,
}: SectionProps) {
  return (
    <section
      className={`rounded-2xl bg-white/[0.02] border border-white/5 ${className ?? ''}`}
    >
      <header className="flex items-center justify-between gap-4 px-6 pt-5 pb-4">
        <h2 className="text-[13px] font-semibold tracking-wide text-white/90">
          {title}
        </h2>
        {trailing}
      </header>
      <div className="px-6 pb-6">{children}</div>
    </section>
  );
}

/**
 * The standard label/value grid used inside a `Section`. Two columns on small
 * screens so short values pack instead of stretching across the page.
 */
export function FieldGrid({
  cols = 3,
  className,
  children,
}: {
  cols?: 2 | 3 | 4;
  className?: string;
  children: ReactNode;
}) {
  const colClass =
    cols === 2
      ? 'sm:grid-cols-2'
      : cols === 3
        ? 'grid-cols-2 md:grid-cols-3'
        : 'grid-cols-2 md:grid-cols-4';

  return (
    <div className={`grid gap-x-8 gap-y-5 ${colClass} ${className ?? ''}`}>
      {children}
    </div>
  );
}

interface FieldProps {
  label: string;
  children: ReactNode;
  className?: string;
}

export function Field({ label, children, className }: FieldProps) {
  return (
    <div className={`flex flex-col gap-1.5 min-w-0 ${className ?? ''}`}>
      <span className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-neutral-500">
        {label}
      </span>
      <div className="text-sm text-white/90 min-w-0">{children}</div>
    </div>
  );
}

interface CopyableValueProps {
  value: string;
  display?: ReactNode;
  href?: string;
  mono?: boolean;
  truncate?: boolean;
  className?: string;
  /** Text size for the value (default `text-sm`). */
  valueClassName?: string;
}

export function CopyableValue({
  value,
  display,
  href,
  mono = true,
  truncate = true,
  className,
  valueClassName = 'text-sm',
}: CopyableValueProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1400);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied to clipboard', { duration: 1500 });
    } catch {
      toast.error('Failed to copy');
    }
  };

  const content = (
    <span
      className={`block ${truncate ? 'truncate' : 'break-all'} ${
        mono ? 'font-mono' : ''
      } ${valueClassName} text-white/90 group-hover:text-white transition-colors`}
      title={value}
    >
      {display ?? value}
    </span>
  );

  return (
    <div
      className={`group flex items-center gap-2 min-w-0 rounded-md px-2 py-1.5 -mx-2 hover:bg-white/[0.03] transition-colors ${
        className ?? ''
      }`}
    >
      <div className="flex-1 min-w-0">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="block min-w-0"
          >
            {content}
          </a>
        ) : (
          content
        )}
      </div>
      <div className="flex items-center gap-0.5 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open in new tab"
            className="p-1 rounded hover:bg-white/10 transition-colors"
          >
            <OpenInNewIcon className="size-4 fill-neutral-400" />
          </a>
        )}
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : 'Copy'}
          className="p-1 rounded hover:bg-white/10 transition-colors"
        >
          {copied ? (
            <CheckIcon className="size-4 fill-emerald-400" />
          ) : (
            <ContentCopyIcon className="size-4 fill-neutral-400" />
          )}
        </button>
      </div>
    </div>
  );
}

interface DisclosureProps {
  label: string;
  count?: number;
  children: ReactNode;
  defaultOpen?: boolean;
}

export function Disclosure({
  label,
  count,
  children,
  defaultOpen = false,
}: DisclosureProps) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();

  return (
    <div className="border-t border-white/5 mt-5 -mx-6 px-6 pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={id}
        className="flex items-center justify-between w-full group"
      >
        <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-neutral-500 group-hover:text-neutral-300 transition-colors">
          {label}
          {typeof count === 'number' && (
            <span className="ml-1.5 text-neutral-600">{count}</span>
          )}
        </span>
        <ExpandMoreIcon
          className={`w-4 h-4 fill-neutral-500 group-hover:fill-neutral-300 transition-all ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        <div id={id} className="pt-4">
          {children}
        </div>
      )}
    </div>
  );
}
